import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Gift, Loader2 } from "lucide-react";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { selectedBranch } = useCart();
  const { settings } = useSettings();
  const welcomePts = settings?.rewards?.enabled ? Number(settings?.rewards?.welcome_bonus_points ?? 20) : 0;
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await register({ ...form, branch_id: selectedBranch?.id || null });
      toast.success(u?.welcome_bonus_points > 0 ? `Account created — ${u.welcome_bonus_points} welcome points added!` : "Account created — welcome to METROS!");
      navigate("/account");
    }
    catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-140px)] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white border border-gray-100 rounded-3xl shadow-card p-8">
        <div className="flex justify-center mb-6"><Logo size="text-3xl" /></div>
        <h1 className="font-heading font-extrabold text-2xl text-center">Create your account</h1>
        <p className="text-sm text-muted-foreground text-center mb-6">Save details, reorder and earn rewards.</p>
        {welcomePts > 0 && (
          <div data-testid="register-welcome-bonus" className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-2xl px-4 py-3 mb-5">
            <div className="bg-metros-red text-white rounded-full p-2 shrink-0"><Gift size={16} /></div>
            <p className="text-sm text-metros-black">Sign up today and get <b className="text-metros-red">{welcomePts} welcome points</b> added to your account instantly.</p>
          </div>
        )}
        {err && <div data-testid="register-error" className="bg-red-50 text-metros-red text-sm rounded-xl px-4 py-3 mb-4">{err}</div>}
        <form onSubmit={submit} className="space-y-3.5">
          <input data-testid="reg-name" required placeholder="Full name" value={form.name} onChange={set("name")}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
          <input data-testid="reg-email" type="email" required placeholder="Email" value={form.email} onChange={set("email")}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
          <input data-testid="reg-phone" placeholder="Phone" value={form.phone} onChange={set("phone")}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
          <div className="relative">
            <input data-testid="reg-password" type={show ? "text" : "password"} required placeholder="Password (min 6 chars)" value={form.password} onChange={set("password")}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 pr-11 focus:ring-2 focus:ring-metros-red focus:outline-none" />
            <button type="button" data-testid="toggle-reg-password" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <button data-testid="register-submit" disabled={busy} className="w-full bg-metros-red hover:bg-metros-redhover text-white rounded-full py-3.5 font-bold flex justify-center items-center gap-2 active:scale-95 transition-all disabled:opacity-60">
            {busy && <Loader2 className="animate-spin" size={18} />} Create Account
          </button>
        </form>
        <p className="text-sm text-center mt-5 text-muted-foreground">
          Already have an account? <Link to="/login" data-testid="go-login" className="text-metros-red font-semibold">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
